import type { TurnPhaseTiming } from '@atbash-labs/ontology-agent-wire'

/**
 * Wall-clock phases of one tool-calling turn, as rendered by the debug
 * trace's turn header. `waiting` and `writing` come from the harness and
 * describe the model; `executing` is derived from the turn's own calls.
 */
export interface TurnPhases {
  /** Request dispatch to first streamed token: queue, prefill, unstreamed reasoning. */
  waitingMs: number
  /** First streamed token to the turn's first tool dispatch. */
  writingMs: number
  /** Wall time across the turn's calls — overlapping calls are counted once. */
  executingMs: number
  totalMs: number
  /** True when call start times were available, so `executingMs` is wall time rather than a sum. */
  executingIsWallTime: boolean
}

interface PhaseInput {
  durationMs?: number | null
  startedAt?: number
  turnTiming?: TurnPhaseTiming
}

/**
 * Wall time across a turn's calls. Tools dispatched in parallel overlap, so
 * summing their durations overstates the turn; with start times we measure
 * first-start to last-finish instead. Without them (older records) the sum
 * is the only available answer and is exact for the serial case.
 */
function executingWallMs(calls: readonly PhaseInput[]): {
  ms: number
  isWallTime: boolean
} {
  const spans = calls.flatMap((call) =>
    typeof call.startedAt === 'number' && typeof call.durationMs === 'number'
      ? [{ start: call.startedAt, end: call.startedAt + call.durationMs }]
      : []
  )
  if (spans.length > 0 && spans.length === calls.length) {
    const start = Math.min(...spans.map((s) => s.start))
    const end = Math.max(...spans.map((s) => s.end))
    return { ms: Math.max(0, end - start), isWallTime: true }
  }
  const summed = calls.reduce(
    (total, call) => total + (typeof call.durationMs === 'number' ? call.durationMs : 0),
    0
  )
  return { ms: summed, isWallTime: false }
}

/**
 * Break one turn's calls into model and tool phases. Returns null when the
 * turn carries no harness timing — a non-streaming agent, or a record
 * written before phase timing existed — so the caller can fall back to the
 * plain per-call view rather than render a bar built on guesses.
 */
export function deriveTurnPhases(calls: readonly PhaseInput[]): TurnPhases | null {
  const timing = calls.find((call) => call.turnTiming !== undefined)?.turnTiming
  if (timing === undefined) return null

  const waitingMs = Math.max(0, timing.waitingMs)
  const writingMs = Math.max(0, timing.writingMs)
  const executing = executingWallMs(calls)

  return {
    waitingMs,
    writingMs,
    executingMs: executing.ms,
    totalMs: waitingMs + writingMs + executing.ms,
    executingIsWallTime: executing.isWallTime
  }
}

export interface PhaseSegment {
  key: 'waiting' | 'writing' | 'executing'
  label: string
  /** What the phase actually measures, for the expanded breakdown. */
  hint: string
  ms: number
  /** Share of the turn, 0–100. */
  percent: number
}

const PHASE_LABELS: Record<PhaseSegment['key'], { label: string; hint: string }> = {
  waiting: {
    label: 'waiting',
    hint: 'queue, prefill, reasoning, and tool input the model never streamed'
  },
  writing: { label: 'writing', hint: 'narration streamed before the tool call' },
  executing: { label: 'tools', hint: 'time inside the tool executors' }
}

/** Ordered segments for the stacked bar and the expanded table. */
export function phaseSegments(phases: TurnPhases): PhaseSegment[] {
  const entries: Array<{ key: PhaseSegment['key']; ms: number }> = [
    { key: 'waiting', ms: phases.waitingMs },
    { key: 'writing', ms: phases.writingMs },
    { key: 'executing', ms: phases.executingMs }
  ]
  return entries.map(({ key, ms }) => ({
    key,
    label: PHASE_LABELS[key].label,
    hint: PHASE_LABELS[key].hint,
    ms,
    percent: phases.totalMs > 0 ? (ms / phases.totalMs) * 100 : 0
  }))
}
